export const SHARE_STATE_SCHEMA_VERSION = 1 as const;
export const SHARE_STATE_TOKEN_QUERY_PARAM = 's' as const;
export const SHARE_STATE_TOKEN_LENGTH = 22;
export const SHARE_STATE_TOKEN_MAX_LENGTH = 32;
export const SHARE_STATE_PAYLOAD_MAX_BYTES = 16_384;

const SHARE_STATE_HASH_HEX_LENGTH = 32;
const BASE62_CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
const BASE62 = BigInt(62);

export interface SharedMapFilterState {
    hiddenSubcategories: string[];
    hiddenRegions: string[];
    regionFilterMode: 'exclude' | 'include';
    hideAll: boolean;
    teleportOnly: boolean;
    unfoundOnly: boolean;
    searchTerm: string;
}

export interface MapFilterShareState {
    version: typeof SHARE_STATE_SCHEMA_VERSION;
    kind: 'mapFilter';
    mapExternalId: string;
    filters: SharedMapFilterState;
}

export interface SharedChecklistShareState {
    version: typeof SHARE_STATE_SCHEMA_VERSION;
    kind: 'sharedChecklist';
    mapExternalId: string;
    title: string;
    description: string;
    pinIds: string[];
}

export type ShareState = MapFilterShareState | SharedChecklistShareState;

export interface CreateShareStateResponse {
    token: string;
}

export interface ShareStateResponse {
    state: ShareState;
}

export type ShareStateErrorCode =
    | 'invalid_body'
    | 'invalid_version'
    | 'invalid_kind'
    | 'invalid_map_external_id'
    | 'invalid_filters'
    | 'invalid_title'
    | 'invalid_description'
    | 'invalid_pin_ids'
    | 'not_found'
    | 'rate_limited'
    | 'token_collision'
    | 'internal_error';

export interface ShareStateErrorResponse {
    error: {
        code: ShareStateErrorCode;
        message: string;
    };
}

function sortUnique(values: string[]): string[] {
    return Array.from(new Set(values)).sort();
}

function base62Encode(value: bigint): string {
    if (value === BigInt(0)) {
        return '0';
    }

    let num = value;
    let str = '';
    while (num > 0) {
        str = `${BASE62_CHARS[Number(num % BASE62)] ?? ''}${str}`;
        num = num / BASE62;
    }
    return str;
}

export function canonicalizeShareState(state: ShareState): ShareState {
    if (state.kind === 'mapFilter') {
        return {
            version: SHARE_STATE_SCHEMA_VERSION,
            kind: 'mapFilter',
            mapExternalId: state.mapExternalId,
            filters: {
                hiddenSubcategories: sortUnique(state.filters.hiddenSubcategories),
                hiddenRegions: sortUnique(state.filters.hiddenRegions),
                regionFilterMode: state.filters.regionFilterMode,
                hideAll: state.filters.hideAll,
                teleportOnly: state.filters.teleportOnly,
                unfoundOnly: state.filters.unfoundOnly,
                searchTerm: state.filters.searchTerm,
            },
        };
    }

    return {
        version: SHARE_STATE_SCHEMA_VERSION,
        kind: 'sharedChecklist',
        mapExternalId: state.mapExternalId,
        title: state.title,
        description: state.description,
        pinIds: sortUnique(state.pinIds),
    };
}

export function serializeShareState(state: ShareState): string {
    return JSON.stringify(canonicalizeShareState(state));
}

async function sha256Hex(value: string): Promise<string> {
    const bytes = new TextEncoder().encode(value);
    const digest = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(digest))
        .map(byte => byte.toString(16).padStart(2, '0'))
        .join('');
}

export async function createShareStateToken(state: ShareState): Promise<string> {
    const hash = await sha256Hex(serializeShareState(state));
    return base62Encode(BigInt(`0x${hash.slice(0, SHARE_STATE_HASH_HEX_LENGTH)}`)).padStart(
        SHARE_STATE_TOKEN_LENGTH,
        '0',
    );
}
